import { z } from 'zod';
import { AppAddonRefSchema } from './addon.js';
/** Stable, lowercase identifier used as client_id, aud, db name, role prefix, registry id (docs/CONVENTIONS.md §B). */
export const AppIdSchema = z.string().regex(/^[a-z][a-z0-9_]{1,30}$/, 'app id: lowercase letters, digits, underscore; 2–31 chars');
export const AppUrlsSchema = z.object({
    dev: z.string().url(),
    staging: z.string().url().optional(),
    prod: z.string().url().optional(),
});
export const AppDatabaseSchema = z.object({
    /** Defaults to the app id. */
    name: z.string().optional(),
    /** Read/write role; defaults to `<id>_app`. */
    role: z.string().optional(),
    /** Tables/views this app publishes in its `public_api` schema for other databases (read-only). */
    publicApi: z.array(z.string()).default([]),
    /** Other databases whose `public_api` this app imports via postgres_fdw as `<name>_ro`. */
    consumes: z.array(z.string()).default([]),
    /** Max pooled connections for this app in PgBouncer (staging/prod). */
    maxConnections: z.number().int().positive().default(20),
});
/** `os-app.json` at the root of every app repository. */
export const AppManifestSchema = z.object({
    $schema: z.string().optional(),
    id: AppIdSchema,
    name: z.string().min(1),
    description: z.string().default(''),
    /** 1–3 characters shown in the launcher mark. */
    icon: z.string().min(1).max(3),
    /** Roles the app understands; OS admins grant these to users. */
    roles: z.array(z.string().regex(/^[a-z][a-z0-9_-]*$/)).min(1),
    urls: AppUrlsSchema,
    /** OIDC redirect URIs (all environments). */
    redirectUris: z.array(z.string().url()).min(1),
    database: AppDatabaseSchema.default({}),
    ports: z.object({ web: z.number().int(), api: z.number().int() }),
    /** Registry display order in the launcher. */
    sortOrder: z.number().int().default(100),
    /** In-repo addons (`packages/<id>`). Not OS apps — see docs/ADDONS.md. */
    addons: z.array(AppAddonRefSchema).default([]),
});
/** Derived identifiers so every tool agrees on names. */
export function appIdentifiers(m) {
    const db = m.database?.name ?? m.id;
    return {
        databaseName: db,
        shadowDatabaseName: `${db}_shadow`,
        appRole: m.database?.role ?? `${m.id}_app`,
        readerRole: `${db}_reader`,
        /** Schema name under which *other* databases see this app's public_api. */
        foreignSchemaName: `${db}_ro`,
        queuePrefix: `${m.id}:`,
    };
}
export function parseManifest(json) {
    return AppManifestSchema.parse(json);
}
//# sourceMappingURL=manifest.js.map